import {
  FC, useEffect, useMemo, useState,
} from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useQuery } from 'react-query'

import { InfoSVGIcon } from '@/components/icons'
import ContactSalesModal from '@/components/shared/ContactSalesModal'
import { getUserFromIdRequest } from '@/api/get-user-from-id-request'
import { getUserProfile, storeUserProfile } from '@/utils/localStorageUtil'
import { formatShortDate } from '@/utils/dateUtils'
import {
  getFreeTrialExpiresAt,
  getVerificationsRemaining,
  isFreeTrialActive,
  isHeadToHeadTrial,
  isUnlimitedVerifications,
} from '@/utils/freeTrialUtil'
import { FREE_TRIAL_STATUS_QUERY_KEY } from '@/lib/constants'

const PUBLIC_ROUTES = [
  '/login',
  '/create-account',
  '/almost-there',
  '/reset-password',
  '/invitation',
  '/logout',
  '/auth/callback',
]

// Marks the document while the banner is on screen so pages can offset for it.
const BANNER_BODY_CLASS = 'has-free-trial-banner'

export const FreeTrialBanner: FC = () => {
  const router = useRouter()
  const { t } = useTranslation('common')
  const [isSalesOpen, setIsSalesOpen] = useState(false)

  const profile = getUserProfile()
  const userId = profile?.userId || profile?.id

  const isAuthenticatedRoute = useMemo(
    () => !PUBLIC_ROUTES.some(route => router.asPath.startsWith(route)),
    [router.asPath],
  )

  const { data: userData, isSuccess } = useQuery(
    [FREE_TRIAL_STATUS_QUERY_KEY, userId],
    async () => {
      const user = await getUserFromIdRequest(userId)
      const currentProfile = getUserProfile()

      if (currentProfile) {
        storeUserProfile({
          ...currentProfile,
          ...user,
          teamName: user.customer.name,
          customerId: user.customer.id,
          email: user.email,
          hasViewedTutorial: user.hasViewedTutorial,
        })
      }

      return user
    },
    {
      enabled: Boolean(userId && isAuthenticatedRoute),
      refetchOnWindowFocus: true,
      refetchOnMount: 'always',
      staleTime: 0,
    },
  )

  // Only fall back to the stored profile until the server has answered. Keeping
  // `??` after a successful fetch pinned the banner to whatever localStorage held
  // the first time it rendered, so a response that omitted `freeTrial` — or any
  // later change — could never move the count off its original value.
  const freeTrial = isSuccess
    ? userData?.customer?.freeTrial
    : profile?.customer?.freeTrial
  const isShowing = isAuthenticatedRoute && isFreeTrialActive(freeTrial)

  // The banner is fixed to the viewport, so the page underneath has to reserve
  // room for it — otherwise it sits on top of whatever is at the bottom of the
  // page (PR #467 review 5). Every `.grid-content` reads the class through
  // `[body.has-free-trial-banner_&]:pb-[141px]`: its own 32px, plus the 24px the
  // banner sits off the edge, its 61px height and a 24px gap.
  useEffect(() => {
    document.body.classList.toggle(BANNER_BODY_CLASS, isShowing)
    return () => document.body.classList.remove(BANNER_BODY_CLASS)
  }, [isShowing])

  if (!isShowing) {
    return null
  }

  const verificationsRemaining = getVerificationsRemaining(freeTrial)
  const expiresAt = getFreeTrialExpiresAt(freeTrial)
  const getSubtitle = (): string => {
    if (isHeadToHeadTrial(freeTrial) && expiresAt) {
      return t('free_trial_banner.expires_on', { date: formatShortDate(expiresAt) })
    }
    if (isUnlimitedVerifications(freeTrial)) {
      return t('free_trial_banner.unlimited_verifications')
    }
    return t('free_trial_banner.verifications_remaining', { count: verificationsRemaining })
  }
  const subtitle = getSubtitle()

  return (
    <>
      {isSalesOpen && <ContactSalesModal onClose={() => setIsSalesOpen(false)} />}
      <div className="free-trial-banner pointer-events-none fixed bottom-[24px] left-1/2 z-[1050] w-[min(800px,calc(100vw-48px))] [transform:translateX(-50%)]">
        <div className="free-trial-banner-content pointer-events-auto box-border flex h-[61px] items-center justify-between gap-[16px] rounded-xl bg-gray-6-filled px-[24px] py-[16px] opacity-100 [border:1px_solid_#191917] [box-shadow:0_8px_24px_rgba(25,25,23,0.08)]">
          <div className="free-trial-banner-content-left flex min-w-0 items-center gap-[16px]">
            <InfoSVGIcon className="free-trial-banner-icon size-[16px] shrink-0 text-gray-2 [&_path]:stroke-current [&_path]:[stroke-opacity:1]" />
            <div className="free-trial-banner-text flex min-w-0 flex-col gap-[2px]">
              <span className="free-trial-banner-title font-[Switzer-SemiBold] text-[14px] font-semibold not-italic leading-[100%] text-gray-1">{t('free_trial_banner.title')}</span>
              <span className="free-trial-banner-subtitle font-[Switzer] text-[12px] font-normal not-italic leading-[110%] text-gray-3">
                {subtitle}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="free-trial-banner-cta shrink-0 cursor-pointer p-0 font-[Switzer-SemiBold] text-[14px] font-semibold not-italic leading-[100%] text-gray-1 [background:transparent] [border:none] hover:text-gray-2"
            onClick={() => setIsSalesOpen(true)}
          >
            {t('free_trial_banner.contact_sales')}
          </button>
        </div>
      </div>
    </>
  )
}

export default FreeTrialBanner
